import { useState, lazy, Suspense } from 'react';
import type { DemoAccounts } from './components/auth/DemoAccountsBox';
import { useCompany } from './contexts/CompanyContext';
import LoadingFallback from './components/LoadingFallback';
import type { CurrentUser, TenantSettings, LinkedCustomer } from './types';
import type { PrefillData } from './hooks/useAppNavigation';
import { getViewPref as readViewPref, setViewPref as writeViewPref } from './utils/viewPreference';
import { MainLandmarkWrapper } from './components/shell/mainLandmark';

// Dev-only shell preview
const ShellPreviewPage = import.meta.env.DEV
  ? lazy(() => import('./pages/ShellPreviewPage'))
  : () => null;

// Lazy load page components for better performance
const LoginPage = lazy(() => import('./pages/LoginPage'));
// Dashboard V1 (classic) and V2 (DataTable/TanStack) — toggle via link in dashboard header
const DashboardV1 = lazy(() => import('./pages/Dashboard'));
const DashboardV2 = lazy(() => import('./pages/DashboardV2'));
const OrderForm = lazy(() => import('./pages/OrderForm'));
// Dev-only: legacy /admin route excluded from production builds
const AdminCombinedPage = import.meta.env.DEV
  ? lazy(() => import('./pages/AdminCombinedPage'))
  : () => null;
const GlobalAdminPage = lazy(() => import('./pages/GlobalAdminPage'));
const GlobalAdminPageV2 = lazy(() => import('./pages/GlobalAdminPageV2'));
const ResellerAdminPage = lazy(() => import('./pages/ResellerAdminPage'));
const ResellerAdminPageV2 = lazy(() => import('./pages/ResellerAdminPageV2'));
const TenantAdminPage = lazy(() => import('./pages/TenantAdminPage'));
const TenantAdminPageV2 = lazy(() => import('./pages/TenantAdminPageV2'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const MyAccount = lazy(() => import('./pages/MyAccount'));
const CreditMemoDetail = lazy(() => import('./pages/CreditMemoDetail'));
const ShipmentDetail = lazy(() => import('./pages/ShipmentDetail'));
const DocsSetupPage = lazy(() => import('./pages/DocsSetupPage'));
// UX-GROWTH H2: in-app help centre
const HelpPage = lazy(() => import('./pages/HelpPage'));
const LegalDocument = lazy(() => import('./pages/LegalDocument'));
// Phase 10: Support tickets (user-facing)
const SupportTickets = lazy(() => import('./pages/SupportTickets'));
// Phase 10: Public extension download page
const ExtensionDownload = lazy(() => import('./pages/ExtensionDownload'));
// Phase 8: Self-service registration + password reset
const AccessRequestPage = lazy(() => import('./pages/AccessRequestPage'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));
// Reseller self-service registration
const ResellerRegistrationPage = lazy(() => import('./pages/ResellerRegistrationPage'));

// --- Type Definitions ---

type FormMode = 'order' | 'quote';

interface AppRouterProps {
  currentUser: CurrentUser | null;
  currentPage: string;
  setCurrentPage: (page: string) => void;
  tenantSettings: TenantSettings | null;
  refreshTenantSettings: () => Promise<void>;
  linkedCustomers: LinkedCustomer[] | null;
  onCustomerSwitch: (customer: LinkedCustomer) => Promise<void>;
  // Navigation
  editingOrderNo: string | null;
  editingQuoteNo: string | null;
  viewingInvoiceNo: string | null;
  viewingCreditMemoNo: string | null;
  viewingShipmentNo: string | null;
  selectedShipToCode: string | null;
  formMode: FormMode;
  prefillOrder: PrefillData | null;
  dashboardRefreshKey: number;
  /** Admin shell tab from the URL (/tntadm/{tab}); null outside the admin shells. */
  adminTab?: string | null;
  onAdminTabChange?: (tab: string, search?: string) => void;
  /** ADMIN-REVIEW X1: record id from /tntadm/{tab}/{id}, and how to open one. */
  adminRecordId?: string | null;
  onOpenAdminRecord?: (tab: string, id: string, search?: string) => void;
  /** UX-GROWTH H2: article slug from /help/{article}, and in-app navigation for its links. */
  helpArticle?: string | null;
  /** Legal document slug from /legal/{slug}. */
  legalSlug?: string | null;
  onNavigate?: (href: string) => void;
  handleNewOrder: (prefillData?: PrefillData | null) => void;
  handleNewQuote: () => void;
  handleEditOrder: (orderNo: string) => void;
  handleEditQuote: (quoteNo: string) => void;
  handleQuoteConverted: () => void;
  handleViewPostedInvoice: (invoiceNo: string, orderNo?: string) => void;
  handleViewCreditMemo: (creditMemoNo: string) => void;
  handleViewShipment: (shipmentNo: string) => void;
  handleBackToDashboard: () => void;
  handleNavigateHome: () => void;
  onMyAccount?: () => void;
  // Auth
  handleLogin: (
    email: string,
    customerId: string,
    portalAccess?: string,
    customerName?: string,
    allowedShipToCodes?: string[] | null,
    role?: string,
    customers?: LinkedCustomer[] | null,
    contactName?: string | null
  ) => void;
  canPlaceOrders: boolean;
  isReadOnly: boolean;
  demoMode: boolean;
  demoAccounts?: DemoAccounts | null;
  /** True when the user was bounced here by an expired session (401 + refresh fail). */
  sessionExpired?: boolean;
}

interface AuthRequiredProps {
  message: string;
}

interface AccessDeniedProps {
  message: string;
  onBack: () => void;
}

/**
 * Handles page rendering based on currentPage + URL sync.
 * Receives all props from App.js.
 */
export default function AppRouter({
  currentUser,
  currentPage,
  setCurrentPage,
  tenantSettings,
  refreshTenantSettings,
  linkedCustomers,
  onCustomerSwitch,
  // Navigation
  editingOrderNo,
  editingQuoteNo,
  viewingInvoiceNo,
  viewingCreditMemoNo,
  viewingShipmentNo,
  selectedShipToCode,
  formMode,
  prefillOrder,
  dashboardRefreshKey,
  adminTab,
  onAdminTabChange,
  adminRecordId,
  onOpenAdminRecord,
  helpArticle,
  legalSlug,
  onNavigate,
  handleNewOrder,
  handleNewQuote,
  handleEditOrder,
  handleEditQuote,
  handleQuoteConverted,
  handleViewPostedInvoice,
  handleViewCreditMemo,
  handleViewShipment,
  handleBackToDashboard,
  handleNavigateHome,
  onMyAccount,
  // Auth
  handleLogin,
  canPlaceOrders,
  isReadOnly,
  demoMode,
  demoAccounts,
  sessionExpired,
}: AppRouterProps) {
  const { company } = useCompany();

  // Unified view preference — stored in localStorage, shared across login, dashboard, admin, order form
  const getViewPref = (): 'v1' | 'v2' => {
    return readViewPref();
  };
  const [viewPref, setViewPref] = useState<'v1' | 'v2'>(getViewPref);
  const toggleViewPref = () => {
    const next = viewPref === 'v2' ? 'v1' : 'v2';
    writeViewPref(next);
    setViewPref(next);
  };

  // Dashboard version driven by unified pref
  const dashboardVersion = viewPref;
  const Dashboard = dashboardVersion === 'v2' ? DashboardV2 : DashboardV1;

  // OrderForm version driven by unified pref
  const orderFormVersion = viewPref;

  // (URL ↔ page sync is gone: the router owns the URL — see routes/router.tsx.)
  const adminTabProps = { tab: adminTab ?? undefined, onTabChange: onAdminTabChange };

  // Inline "auth required" fallback for protected pages
  const AuthRequired = ({ message }: AuthRequiredProps) => (
    <div style={{ padding: '40px', textAlign: 'center' }}>
      <h2>Authentication Required</h2>
      <p style={{ color: 'var(--color-error)', marginTop: '20px' }}>{message}</p>
      <button
        onClick={() => setCurrentPage('login')}
        style={{
          marginTop: '20px',
          padding: '10px 20px',
          background: 'var(--color-primary)',
          color: 'var(--color-text-inverse)',
          border: 'none',
          borderRadius: '6px',
          cursor: 'pointer'
        }}
      >
        Go to Login
      </button>
    </div>
  );

  const AccessDenied = ({ message, onBack }: AccessDeniedProps) => (
    <div style={{ padding: '40px', textAlign: 'center' }}>
      <h2>Access Denied</h2>
      <p style={{ color: 'var(--color-error)', marginTop: '20px' }}>{message}</p>
      <button
        onClick={onBack}
        style={{
          marginTop: '20px',
          padding: '10px 20px',
          background: 'var(--color-primary)',
          color: 'var(--color-text-inverse)',
          border: 'none',
          borderRadius: '6px',
          cursor: 'pointer'
        }}
      >
        Back to Dashboard
      </button>
    </div>
  );

  return (
    <MainLandmarkWrapper className="app-main">
      <Suspense fallback={<LoadingFallback />}>
        {currentPage === 'login' && (
          <LoginPage
            onLogin={handleLogin}
            demoMode={demoMode}
            demoAccounts={demoAccounts}
            onRequestAccess={() => setCurrentPage('access-request')}
            registrationMode={tenantSettings?.features?.registrationMode}
            mfaMode={tenantSettings?.security?.mfa || 'future'}
            onResellerRegister={() => setCurrentPage('reseller-register')}
            sessionExpired={sessionExpired}
          />
        )}

        {currentPage === 'docs-setup' && (
          <DocsSetupPage />
        )}

        {currentPage === 'help' && (
          <HelpPage article={helpArticle} onNavigate={onNavigate} />
        )}

        {currentPage === 'legal' && legalSlug && (
          <LegalDocument slug={legalSlug} />
        )}

        {currentPage === 'dashboard' && currentUser && (
          <Dashboard
            key={`${dashboardVersion}-${dashboardRefreshKey}`}
            customerId={currentUser.customerId}
            email={currentUser.email}
            currentUser={currentUser}
            tenantSettings={tenantSettings ?? undefined}
            linkedCustomers={linkedCustomers ?? undefined}
            onCustomerSwitch={onCustomerSwitch}
            onNewOrder={handleNewOrder}
            onEditOrder={handleEditOrder}
            onNewQuote={handleNewQuote}
            onEditQuote={handleEditQuote}
            onViewPostedInvoice={handleViewPostedInvoice}
            onViewCreditMemo={handleViewCreditMemo}
            onViewShipment={handleViewShipment}
            onMyAccount={onMyAccount}
            canPlaceOrders={canPlaceOrders}
            isReadOnly={isReadOnly}
            onSwitchVersion={toggleViewPref}
            dashboardVersion={dashboardVersion}
          />
        )}

        {currentPage === 'dashboard' && !currentUser && (
          <AuthRequired message="Please log in to access the dashboard." />
        )}

        {currentPage === 'order-form' && currentUser && (
          <OrderForm
            key={`order-form-${orderFormVersion}-${editingOrderNo || editingQuoteNo || 'new'}-${viewingInvoiceNo || 'none'}-${prefillOrder ? 'prefill' : 'normal'}-${formMode}`}
            customerId={currentUser.customerId}
            email={currentUser.email}
            shipToCode={selectedShipToCode ?? undefined}
            editingOrderNo={editingOrderNo ?? undefined}
            editingQuoteNo={editingQuoteNo ?? undefined}
            viewingInvoiceNo={viewingInvoiceNo ?? undefined}
            formMode={formMode}
            prefillOrder={prefillOrder ?? undefined}
            allowedShipToCodes={currentUser.allowedShipToCodes}
            tenantSettings={tenantSettings ?? undefined}
            onBack={handleBackToDashboard}
            onQuoteConverted={handleQuoteConverted}
            onSwitchVersion={toggleViewPref}
            orderFormVersion={orderFormVersion}
          />
        )}

        {currentPage === 'order-form' && !currentUser && (
          <AuthRequired message="Please log in to create or edit orders." />
        )}

        {currentPage === 'credit-memo-detail' && currentUser && (
          <CreditMemoDetail
            creditMemoNo={viewingCreditMemoNo!}
            tenantSettings={tenantSettings ?? undefined}
            onBack={handleBackToDashboard}
          />
        )}

        {currentPage === 'shipment-detail' && currentUser && (
          <ShipmentDetail
            shipmentNo={viewingShipmentNo!}
            tenantSettings={tenantSettings ?? undefined}
            onBack={handleBackToDashboard}
          />
        )}

        {currentPage === 'settings' && currentUser && (
          <SettingsPage
            currentUser={currentUser}
            onBack={handleBackToDashboard}
          />
        )}

        {currentPage === 'settings' && !currentUser && (
          <AuthRequired message="Please log in to access settings." />
        )}

        {currentPage === 'my-account' && currentUser && (
          <MyAccount
            currentUser={currentUser}
            tenantSettings={tenantSettings ?? undefined}
            onBack={handleBackToDashboard}
          />
        )}

        {currentPage === 'my-account' && !currentUser && (
          <AuthRequired message="Please log in to access your account." />
        )}

        {/* Phase 10: Support Tickets (user-facing) */}
        {currentPage === 'support-tickets' && currentUser && (
          <SupportTickets
            onBack={handleNavigateHome}
            backLabel={currentUser.role && currentUser.role !== 'user' ? 'Back to Admin' : 'Back to Dashboard'}
            userRole={currentUser.role}
          />
        )}

        {currentPage === 'support-tickets' && !currentUser && (
          <AuthRequired message="Please log in to view support tickets." />
        )}

        {/* Phase 10: Public Extension Downloads (no auth required) */}
        {currentPage === 'downloads' && (
          <ExtensionDownload onBack={handleBackToDashboard} />
        )}

        {/* Phase 8: Public access request form (no auth required) */}
        {currentPage === 'access-request' && (
          <AccessRequestPage
            onLoginClick={() => setCurrentPage('login')}
            tenantId={company?.id ?? ''}
            tenantName={company?.name ?? 'Goodstuff Portal'}
          />
        )}

        {/* Phase 8: Set/reset password via token (no auth required) */}
        {currentPage === 'set-password' && (
          <ResetPasswordPage onSuccess={() => setCurrentPage('login')} />
        )}

        {/* Reseller self-service registration (no auth required) */}
        {currentPage === 'reseller-register' && (
          <ResellerRegistrationPage onLoginClick={() => setCurrentPage('login')} />
        )}

        {/* Global Admin Portal — V1/V2 toggle via localStorage */}
        {currentPage === 'glbadm' && currentUser && currentUser.role === 'global_admin' && (
          (() => {
            if (readViewPref() === 'v2') { return <GlobalAdminPageV2 currentUser={currentUser} {...adminTabProps} recordId={adminRecordId ?? undefined} onOpenRecord={onOpenAdminRecord} />; }
            return <GlobalAdminPage currentUser={currentUser} />;
          })()
        )}

        {currentPage === 'glbadm' && (!currentUser || currentUser.role !== 'global_admin') && (
          <AccessDenied message="Global administrator access required." onBack={handleBackToDashboard} />
        )}

        {/* Reseller Admin Portal — V1/V2 toggle */}
        {currentPage === 'rsladm' && currentUser && currentUser.role === 'reseller_admin' && (
          (() => {
            if (readViewPref() === 'v2') { return <ResellerAdminPageV2 currentUser={currentUser} {...adminTabProps} />; }
            return <ResellerAdminPage currentUser={currentUser} />;
          })()
        )}

        {currentPage === 'rsladm' && (!currentUser || currentUser.role !== 'reseller_admin') && (
          <AccessDenied message="Reseller administrator access required." onBack={handleBackToDashboard} />
        )}

        {/* Tenant Admin Portal - reseller_admin arrives here via impersonation — V1/V2 toggle */}
        {currentPage === 'tntadm' && currentUser && (currentUser.role === 'global_admin' || currentUser.role === 'tenant_admin' || currentUser.role === 'reseller_admin') && (
          (() => {
            if (readViewPref() === 'v2') { return <TenantAdminPageV2 currentUser={currentUser} onSettingsUpdate={refreshTenantSettings} {...adminTabProps} recordId={adminRecordId ?? undefined} onOpenRecord={onOpenAdminRecord} />; }
            return <TenantAdminPage currentUser={currentUser} onSettingsUpdate={refreshTenantSettings} />;
          })()
        )}

        {currentPage === 'tntadm' && (!currentUser || (currentUser.role !== 'global_admin' && currentUser.role !== 'tenant_admin' && currentUser.role !== 'reseller_admin')) && (
          <AccessDenied message="Tenant administrator access required." onBack={handleBackToDashboard} />
        )}

        {currentPage === 'not-found' && (
          <div style={{ padding: '40px', textAlign: 'center' }} role="alert">
            <h2>Page not found</h2>
            <p style={{ marginTop: '12px' }}>That link does not match a page in the portal.</p>
            <button
              onClick={handleNavigateHome}
              style={{ marginTop: '20px', padding: '10px 20px', background: 'var(--color-primary)', color: 'var(--color-text-inverse)', border: 'none', borderRadius: '6px', cursor: 'pointer' }}
            >
              Go to home page
            </button>
          </div>
        )}

        {/* Legacy /admin route - dev only */}
        {import.meta.env.DEV && currentPage === 'admin' && currentUser && (currentUser.role === 'global_admin' || currentUser.role === 'tenant_admin') && (
          <AdminCombinedPage currentUser={currentUser} />
        )}

        {/* Dev-only shell preview — visit /shell-preview to see the CustomerShell components */}
        {import.meta.env.DEV && currentPage === 'shell-preview' && (
          <ShellPreviewPage />
        )}

        {import.meta.env.DEV && currentPage === 'admin' && (!currentUser || (currentUser.role !== 'global_admin' && currentUser.role !== 'tenant_admin')) && (
          <AccessDenied message="You do not have permission to access this page." onBack={handleBackToDashboard} />
        )}
      </Suspense>
    </MainLandmarkWrapper>
  );
}
